/**
 * Module dependencies.
 */

import { GetStaticProps, NextPage } from 'next/types';
import { LayoutProps } from 'src/components/layout';
import { theme as amtrolAlfaTheme } from 'src/styles/theme';
import { bannersKeys } from 'src/core/content-config/banners';
import { contentConfig } from 'src/core/content-config/human-resources';
import { fetchBanner } from 'src/api/banners/fetch-banner';
import { useLocalizedRoute } from 'src/core/utils/routes';
import BannerSection, { Banner } from 'src/components/sections/banner-section';
import Metatags from 'src/components/core/metatags';
import NextSection from 'src/components/sections/next-section';
import TextImageSection from 'src/components/sections/text-image-section';
import styled from 'styled-components';
import useBanner from 'src/api/banners/use-banner';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = {
  banners: Banner[];
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  position: relative;
`;

/**
 * `HumanResources` page.
 */

const HumanResources: NextPage<Props> = props => {
  const { translate } = useTranslate();
  const routeResolver = useLocalizedRoute();
  const { data: bannersData, isSuccess: isSuccessBanners } = useBanner({
    initialData: props?.banners,
    slug: bannersKeys.humanResources
  });

  return (
    <>
      <Metatags
        description={translate('human-resources:page.description')}
        pageTitle={translate('human-resources:page.title')}
        title={translate('human-resources:page.title')}
      />

      <Wrapper>
        {isSuccessBanners && <BannerSection banners={bannersData} />}

        <TextImageSection
          bgColor={amtrolAlfaTheme.colors.backgrounds.blue01}
          blocks={contentConfig.innovationBlocks}
          description={translate(
            'human-resources:sections.innovation.description'
          )}
          imageUrl={'/static/images/manufacture-2.jpg'}
          subtitleOne={translate(
            'human-resources:sections.innovation.subtitleOne'
          )}
          subtitleTwo={translate(
            'human-resources:sections.innovation.subtitleTwo'
          )}
          titleOne={translate('human-resources:sections.innovation.titleOne')}
          titleTwo={translate('human-resources:sections.innovation.titleTwo')}
        />

        <NextSection
          bgColor={amtrolAlfaTheme.colors.backgrounds.orange02}
          imageUrl={'/static/images/innovation-square.jpg'}
          nextSectionUrl={routeResolver('innovation')}
          title={translate('human-resources:sections.next.title')}
        />
      </Wrapper>
    </>
  );
};

/**
 * Export `getStaticProps`.
 */

export const getStaticProps: GetStaticProps<Props & LayoutProps> = async ({
  locale
}) => {
  const banners = await fetchBanner({
    locale,
    slug: bannersKeys.humanResources
  });

  return {
    props: {
      banners,
      hasMobileScrollDownIndicator: true,
      hasScrollDownIndicator: true
    },
    revalidate: 60 * 60 * 24
  };
};

/**
 * Export `HumanResources` page.
 */

export default HumanResources;
